import React, { lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
export function KidsDecodeDetail() {
  const reportData = {
    name: 'Kids Decode',
    estimatedTime: '3–4 minutes',
    depth: 'Full diagnostic (L2–L4)',
    price: '₹999',
    analyses: ['Baseline temperament and processing patterns', 'Energy expression and environmental response', 'Emotional regulation and expression patterns', 'Developmental tendencies and adaptive strategies'],
    receives: [{
      level: 'L2',
      description: 'Temperament mechanics and expression patterns'
    }, {
      level: 'L3',
      description: 'Developmental context and adaptive responses'
    }, {
      level: 'L4',
      description: 'Support leverage points and growth patterns'
    }, {
      level: 'Optional',
      description: 'Cross-context developmental implications'
    }],
    doesNotDo: ['Does not provide parenting advice', 'Does not evaluate behavior as good or bad', 'Does not diagnose developmental disorders', 'Does not replace therapy or professional assessment'],
    inputsRequired: {
      baseline: true,
      baselineItems: ["Child's name, date of birth, time and place of birth", 'Optional observational inputs (energy, change response, emotional expression)'],
      reportSpecific: 'Child-specific baseline and optional observations',
      estimatedInputTime: '3–4 minutes'
    }
  };
  const handleUnlockReport = () => {
    // Store selected report type (Class C - child baseline only)
    localStorage.setItem('selectedReport', 'kids-decode');
  };
  return <>
      <Helmet data-id="element-1802">
        <title data-id="element-1803">{reportData.name} — Decode | Lazy Monk Labs</title>
        <meta name="description" content={`Full diagnostic report: ${reportData.name}`} data-id="element-1804" />
        <meta name="robots" content="noindex, nofollow" data-id="element-1805" />
        <link rel="canonical" href="https://lazymonklabs.com/kids-decode-detail" data-id="element-1806" />

        {/* Google Analytics */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-QCXDHW3F0P" data-id="element-1807"></script>
        <script data-id="element-1808">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-QCXDHW3F0P');
          `}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-1809">
        {/* Navigation */}
        <header data-id="element-1810">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-1811">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-1812">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-1813">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-1814" />
                <span className="text-lg font-medium tracking-tight" data-id="element-1815">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-1816">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-1817">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" aria-label="Contact us on WhatsApp" data-id="element-1818">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        {/* Main Content */}
        <main className="pt-32 pb-24 px-6" data-id="element-1819">
          <div className="max-w-3xl mx-auto" data-id="element-1820">
            {/* Page Header */}
            <div className="mb-16" data-id="element-1821">
              <h1 className="text-4xl md:text-5xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1822">
                {reportData.name}
              </h1>
              <p className="text-sm text-[#F5F5F5]/50 mb-4" data-id="element-1823">
                Full diagnostic report
              </p>
              <div className="flex flex-col sm:flex-row gap-4 text-xs text-[#F5F5F5]/40" data-id="element-1824">
                <span data-id="element-1825">
                  Estimated time to complete: {reportData.estimatedTime}
                </span>
                <span className="hidden sm:inline" data-id="element-1826">·</span>
                <span data-id="element-1827">Report depth: {reportData.depth}</span>
              </div>
            </div>

            {/* Section 1: What This Report Analyzes */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1828">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-1829">
                What this report analyzes
              </h2>
              <ul className="space-y-4" data-id="element-1830">
                {reportData.analyses.map((item, index) => <li key={index} className="flex gap-4" data-id="element-1831">
                    <span className="text-[#F5F5F5]/30 mt-1" data-id="element-1832">•</span>
                    <span className="text-[#F5F5F5]/70 leading-relaxed flex-1" data-id="element-1833">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </section>

            {/* Section 2: What You'll Receive */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1834">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-1835">
                What you'll receive
              </h2>
              <div className="space-y-4" data-id="element-1836">
                {reportData.receives.map((item, index) => <div key={index} className="flex gap-4" data-id="element-1837">
                    <span className="text-[#F5F5F5]/50 font-medium text-sm" data-id="element-1838">
                      {item.level}
                    </span>
                    <span className="text-[#F5F5F5]/70 leading-relaxed flex-1" data-id="element-1839">
                      {item.description}
                    </span>
                  </div>)}
              </div>
            </section>

            {/* Section 3: What This Report Does Not Do */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1840">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-1841">
                What this report does not do
              </h2>
              <ul className="space-y-4" data-id="element-1842">
                {reportData.doesNotDo.map((item, index) => <li key={index} className="flex gap-4" data-id="element-1843">
                    <span className="text-[#F5F5F5]/30 mt-1" data-id="element-1844">•</span>
                    <span className="text-[#F5F5F5]/70 leading-relaxed flex-1" data-id="element-1845">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </section>

            {/* Section 4: Inputs Required */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1846">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-1847">
                Inputs required
              </h2>
              <div className="space-y-6" data-id="element-1848">
                {reportData.inputsRequired.baseline && <div data-id="element-1849">
                    <p className="text-[#F5F5F5]/70 mb-2" data-id="element-1850">
                      Child baseline setup:
                    </p>
                    <ul className="space-y-2 ml-4" data-id="element-1851">
                      {reportData.inputsRequired.baselineItems.map((item, index) => <li key={index} className="flex gap-4" data-id="element-1852">
                            <span className="text-[#F5F5F5]/30 mt-1" data-id="element-1853">•</span>
                            <span className="text-[#F5F5F5]/60 leading-relaxed flex-1" data-id="element-1854">
                              {item}
                            </span>
                          </li>)}
                    </ul>
                  </div>}
                <div data-id="element-1855">
                  <p className="text-xs text-[#F5F5F5]/50" data-id="element-1856">
                    Estimated input time:{' '}
                    {reportData.inputsRequired.estimatedInputTime}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5: Price & Purchase */}
            <section className="mb-12" data-id="element-1857">
              <div className="border border-[#2B2B2B] p-8" data-id="element-1858">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6" data-id="element-1859">
                  <div data-id="element-1860">
                    <p className="text-3xl font-medium mb-2" data-id="element-1861">
                      {reportData.price}
                    </p>
                    <p className="text-xs text-[#F5F5F5]/50" data-id="element-1862">
                      One-time purchase · No subscription
                    </p>
                  </div>
                  <Link to="/login" onClick={handleUnlockReport} className="inline-block px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-center font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg whitespace-nowrap" data-id="element-1863">
                    Unlock Full Report
                  </Link>
                </div>
              </div>
            </section>
          </div>
        </main>

        {/* Minimal Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" role="contentinfo" data-id="element-1864">
          <div className="max-w-7xl mx-auto text-center" data-id="element-1865">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-1866">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}